import { useEffect, useMemo, useRef, useState } from 'react';
import { analyticsService } from '../services/analyticsService';
import { normalizeQueryResult } from '../adapters/normalizeQueryResult';
import { buildQuerySpec } from '../adapters/buildQuerySpec';
import { getChartConfig } from '../utils/chartTypeMapping';
import { aggregationNeedsMeasureField } from '../constants/aggregations';
import { DATASET_INTERVENE, DATASET_WHATSAPP, usesIndependentScope } from '../constants/datasetScope';

export default function useReportPreview(builderState, orgId, debounceMs = 300) {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef(null);
  const requestRef = useRef(0);

  const previewKey = useMemo(() => {
    const {
      dataset,
      accountId,
      chartType,
      dimension,
      metric,
      aggregation,
      measureField,
    } = builderState || {};

    const needsAccount = !usesIndependentScope(dataset);
    if (!dataset || !chartType || !metric) return '';
    if (needsAccount && !accountId) return '';

    const chartConfig = getChartConfig(chartType);
    const requiresDimension = chartConfig.requiresDimension && dataset !== DATASET_WHATSAPP;
    if (requiresDimension && !dimension) return '';
    if (aggregationNeedsMeasureField(aggregation) && !measureField) return '';

    const querySpec = buildQuerySpec(builderState);
    if (dataset === DATASET_INTERVENE && chartType === 'table') {
      querySpec.include_sessions = false;
    }
    return JSON.stringify(querySpec);
  }, [builderState]);

  useEffect(() => {
    if (!previewKey) {
      requestRef.current += 1;
      setPreview(null);
      setPreviewError('');
      setIsLoading(false);
      return undefined;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setIsLoading(true);
    setPreviewError('');

    timerRef.current = setTimeout(async () => {
      const querySpec = JSON.parse(previewKey);
      const res = await analyticsService.previewQuery(querySpec, orgId);
      if (requestRef.current !== requestId) return;
      if (res.ok) {
        setPreview(normalizeQueryResult(res.data));
      } else {
        setPreview(null);
        const message = typeof res.data === 'string'
          ? res.data
          : res.data?.message || 'Could not load preview.';
        setPreviewError(message);
      }
      setIsLoading(false);
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [previewKey, orgId, debounceMs]);

  return { preview, previewError, isLoading };
}
